import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  SmilePlus,
  Loader2,
  TrendingUp,
  Activity,
} from "lucide-react";
import {
  BarChart,
  Bar,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import apiService from "../services/apiService";
import webAuthService from "../services/webAuthService";
import { useRole } from "../contexts/RoleContext";

const MeetingAnalytics = ({ overrideUserId = null }) => {
  const { meetingId, groupId } = useParams();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [evaluationData, setEvaluationData] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [analyticsType, setAnalyticsType] = useState("meeting"); // "meeting", "group", or "user"

  // RBAC: Get user's permissions
  const { permissions, loading: roleLoading } = useRole();

  useEffect(() => {
    const initUser = async () => {
      // Use overrideUserId if provided (for admin viewing other users), otherwise get logged-in user
      if (overrideUserId) {
        setUserId(overrideUserId);
      } else {
        const uid = await webAuthService.getUserId();
        setUserId(uid);
      }
    };
    initUser();
  }, [overrideUserId]);

  // RBAC: Ensure active tab is valid based on permissions
  useEffect(() => {
    if (!roleLoading && permissions) {
      const availableTabs = [
        { id: "overview", permission: "can_view_overview" },
        { id: "sales", permission: "can_view_sales_performance" },
        { id: "client", permission: "can_view_client_insights" },
        { id: "sentiment", permission: "can_view_sentiment" },
      ].filter((tab) => permissions[tab.permission]);

      // If current active tab is not available, switch to first available tab
      if (availableTabs.length > 0) {
        const currentTabAvailable = availableTabs.some(
          (tab) => tab.id === activeTab
        );
        if (!currentTabAvailable) {
          setActiveTab(availableTabs[0].id);
        }
      }
    }
  }, [roleLoading, permissions, activeTab]);

  useEffect(() => {
    if (groupId) {
      setAnalyticsType("group");
    } else if (!meetingId && !groupId) {
      // No meetingId or groupId means user-level analytics
      setAnalyticsType("user");
    } else {
      setAnalyticsType("meeting");
    }

    if (userId) {
      fetchAnalytics();
    }
  }, [userId, meetingId, groupId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build API URLs based on analytics type
      let evalUrl, sentimentUrl;
      if (groupId) {
        // Group analytics - use group_id query parameter
        evalUrl = `/meetings/evaluation?user_id=${userId}&group_id=${groupId}`;
        sentimentUrl = `/meetings/sentiment?user_id=${userId}&group_id=${groupId}`;
      } else if (meetingId) {
        // Individual meeting analytics - use meeting_id query parameter
        evalUrl = `/meetings/evaluation?user_id=${userId}&meeting_id=${meetingId}`;
        sentimentUrl = `/meetings/sentiment?user_id=${userId}&meeting_id=${meetingId}`;
      } else {
        // User analytics - only user_id parameter
        evalUrl = `/meetings/evaluation?user_id=${userId}`;
        sentimentUrl = `/meetings/sentiment?user_id=${userId}`;
      }

      // Fetch evaluation and sentiment data independently (don't fail if one fails)
      let evalResponse = null;
      let sentimentResponse = null;
      let evalError = null;
      let sentimentError = null;

      // Fetch evaluation data
      try {
        evalResponse = await apiService.get(evalUrl);
      } catch (err) {
        console.warn("[Analytics] Evaluation fetch failed:", err);
        evalError = err.message || "Failed to load evaluation data";
      }

      // Fetch sentiment data
      try {
        sentimentResponse = await apiService.get(sentimentUrl);
      } catch (err) {
        console.warn("[Analytics] Sentiment fetch failed:", err);
        sentimentError = err.message || "Failed to load sentiment data";
      }

      // Handle evaluation data
      if (evalResponse) {
        setEvaluationData({
          sales_evaluation: evalResponse.sales_evaluation || {},
          client_evaluation: evalResponse.client_evaluation || {},
        });
      } else {
        setEvaluationData(null);
      }

      // Handle sentiment data
      if (sentimentResponse) {
        setSentimentData(sentimentResponse.sentiment || sentimentResponse);
      } else {
        setSentimentData(null);
      }

      // Set error only if BOTH APIs failed
      if (evalError && sentimentError) {
        setError("Failed to load analytics data. " + evalError);
      } else if (evalError) {
        // Only evaluation failed - show partial error
        console.info(
          "[Analytics] Showing sentiment data only (evaluation unavailable)"
        );
      } else if (sentimentError) {
        // Only sentiment failed - show partial error
        console.info(
          "[Analytics] Showing evaluation data only (sentiment unavailable)"
        );
      }
    } catch (err) {
      console.error("[Analytics] Error fetching data:", err);
      setError(err.message || "Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const getNumericValue = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const getSentimentColor = (classification) => {
    if (!classification) return "#94a3b8";
    const lower = classification.toLowerCase();
    if (lower.includes("positive")) return "#e04665";
    if (lower.includes("negative")) return "#ef4444";
    return "#f59e0b";
  };

  const getSentimentEmoji = (classification) => {
    if (!classification) return "😐";
    const lower = classification.toLowerCase();
    if (lower.includes("positive")) return "😊";
    if (lower.includes("negative")) return "😔";
    return "😐";
  };

  const getSectionColor = (sectionKey) => {
    const lower = sectionKey.toLowerCase();
    if (lower.includes("red flags") || lower.includes("concern"))
      return "#ef4444";
    if (lower.includes("engagement")) return "#8b5cf6";
    if (lower.includes("positive") || lower.includes("highlight"))
      return "#10b981";
    if (lower.includes("trajectory")) return "#3b82f6";
    if (lower.includes("emotional") || lower.includes("moments"))
      return "#e04665";
    return "#6b7280";
  };

  const renderScoreCard = (title, score, maxScore, emoji, color) => {
    const percentage = (getNumericValue(score) / maxScore) * 100;

    return (
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          padding: "32px",
          border: "1px solid #e5e7eb",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 12px 24px rgba(0, 0, 0, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-20px",
            right: "-20px",
            fontSize: "120px",
            opacity: "0.05",
            transform: "rotate(-15deg)",
          }}
        >
          {emoji}
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              fontSize: "13px",
              color: "#6b7280",
              fontWeight: "700",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              marginBottom: "12px",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "64px",
              fontWeight: "900",
              color: color,
              lineHeight: "1",
              marginBottom: "12px",
              fontFamily: "'Space Mono', monospace",
            }}
          >
            {getNumericValue(score)}
            <span style={{ fontSize: "36px", color: "#9ca3af" }}>
              /{maxScore}
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: "8px",
              background: "#f3f4f6",
              borderRadius: "4px",
              overflow: "hidden",
              marginTop: "16px",
            }}
          >
            <div
              style={{
                width: `${percentage}%`,
                height: "100%",
                background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)`,
                borderRadius: "4px",
                transition: "width 1.5s ease-out",
                boxShadow: `0 0 10px ${color}40`,
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderSalesEvaluation = (data) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "60px",
            textAlign: "center",
            border: "1px solid #e5e7eb",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>📊</div>
          <p style={{ color: "#94a3b8", fontSize: "18px" }}>
            No sales evaluation data available
          </p>
        </div>
      );
    }

    const overallScore = data["Overall Salesperson Performance Score"];

    return (
      <div>
        {/* Overall Score */}
        {overallScore !== undefined && (
          <div style={{ marginBottom: "32px" }}>
            {renderScoreCard(
              "Overall Performance",
              getNumericValue(overallScore),
              10,
              "🎯",
              "#e04665"
            )}
          </div>
        )}

        {/* Detailed Sections */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
            gap: "24px",
          }}
        >
          {Object.entries(data)
            .filter(([key]) => key !== "Overall Salesperson Performance Score")
            .map(([sectionKey, sectionData]) => {
              if (!sectionData || typeof sectionData !== "object") return null;

              return (
                <div
                  key={sectionKey}
                  style={{
                    background: "white",
                    borderRadius: "20px",
                    padding: "32px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    transition: "transform 0.3s, box-shadow 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 24px rgba(0, 0, 0, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 6px rgba(0, 0, 0, 0.1)";
                  }}
                >
                  <h3
                    style={{
                      fontSize: "22px",
                      fontWeight: "800",
                      marginBottom: "24px",
                      color: "#1f2937",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "28px",
                      }}
                    >
                      {sectionKey.includes("Communication")
                        ? "💬"
                        : sectionKey.includes("Relationship")
                        ? "🤝"
                        : sectionKey.includes("Product")
                        ? "📦"
                        : sectionKey.includes("Problem")
                        ? "🔧"
                        : sectionKey.includes("Closing")
                        ? "✅"
                        : "⭐"}
                    </span>
                    {sectionKey}
                  </h3>

                  {Object.entries(sectionData).map(([key, value]) => (
                    <div key={key} style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#6b7280",
                          marginBottom: "10px",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {key}
                      </div>

                      {typeof value === "number" ||
                      !isNaN(parseFloat(value)) ? (
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: "8px",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "36px",
                                fontWeight: "900",
                                color:
                                  getNumericValue(value) > 7
                                    ? "#e04665"
                                    : getNumericValue(value) > 5
                                    ? "#3b82f6"
                                    : "#f59e0b",
                                fontFamily: "'Space Mono', monospace",
                              }}
                            >
                              {getNumericValue(value)}
                            </span>
                            <span
                              style={{
                                fontSize: "18px",
                                color: "#9ca3af",
                                fontWeight: "700",
                              }}
                            >
                              /10
                            </span>
                          </div>
                          <div
                            style={{
                              width: "100%",
                              height: "8px",
                              background: "#f3f4f6",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${
                                  (getNumericValue(value) / 10) * 100
                                }%`,
                                height: "100%",
                                background:
                                  getNumericValue(value) > 7
                                    ? "linear-gradient(90deg, #e04665 0%, #e04665dd 100%)"
                                    : getNumericValue(value) > 5
                                    ? "linear-gradient(90deg, #3b82f6 0%, #3b82f6dd 100%)"
                                    : "linear-gradient(90deg, #f59e0b 0%, #f59e0bdd 100%)",
                                borderRadius: "4px",
                                boxShadow:
                                  getNumericValue(value) > 7
                                    ? "0 0 10px #e0466560"
                                    : getNumericValue(value) > 5
                                    ? "0 0 10px #3b82f660"
                                    : "0 0 10px #f59e0b60",
                                transition: "width 1s ease-out",
                              }}
                            />
                          </div>
                        </div>
                      ) : Array.isArray(value) ? (
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: "20px",
                            color: "#94a3b8",
                            lineHeight: "1.8",
                          }}
                        >
                          {value.map((item, idx) => (
                            <li key={idx} style={{ marginBottom: "6px" }}>
                              <span style={{ color: "#4b5563" }}>{item}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p
                          style={{
                            color: "#94a3b8",
                            fontSize: "14px",
                            margin: 0,
                            lineHeight: "1.6",
                          }}
                        >
                          {String(value)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const renderClientEvaluation = (data) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "60px",
            textAlign: "center",
            border: "1px solid #e5e7eb",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>👥</div>
          <p style={{ color: "#94a3b8", fontSize: "18px" }}>
            No client evaluation data available
          </p>
        </div>
      );
    }

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
          gap: "24px",
        }}
      >
        {Object.entries(data).map(([sectionKey, sectionData]) => {
          if (!sectionData || typeof sectionData !== "object") return null;

          return (
            <div
              key={sectionKey}
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "32px",
                border: "1px solid #e5e7eb",
                backdropFilter: "blur(20px)",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.3s, box-shadow 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow =
                  "0 30px 80px rgba(0, 0, 0, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 25px 70px rgba(0, 0, 0, 0.4)";
              }}
            >
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  marginBottom: "24px",
                  color: "#1f2937",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "28px" }}>
                  {sectionKey.includes("Willingness") ||
                  sectionKey.includes("Readiness")
                    ? "🎯"
                    : sectionKey.includes("Interests") ||
                      sectionKey.includes("Engagement")
                    ? "💡"
                    : sectionKey.includes("Potential")
                    ? "⭐"
                    : sectionKey.includes("Opportunities")
                    ? "🚀"
                    : sectionKey.includes("Concerns") ||
                      sectionKey.includes("Objections")
                    ? "⚠️"
                    : sectionKey.includes("Next Steps")
                    ? "📋"
                    : "💼"}
                </span>
                {sectionKey}
              </h3>

              {Object.entries(sectionData).map(([key, value]) => (
                <div
                  key={key}
                  style={{
                    marginBottom: "20px",
                    padding: "16px",
                    background: "white",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#e04665",
                      marginBottom: "10px",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {key}
                  </div>

                  {Array.isArray(value) ? (
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "20px",
                        color: "#4b5563",
                        lineHeight: "1.8",
                      }}
                    >
                      {value.map((item, idx) => (
                        <li
                          key={idx}
                          style={{
                            marginBottom: "8px",
                            position: "relative",
                          }}
                        >
                          <span style={{ color: "#1f2937" }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p
                      style={{
                        color: "#4b5563",
                        fontSize: "15px",
                        margin: 0,
                        lineHeight: "1.7",
                      }}
                    >
                      {String(value)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSentimentAnalysis = (data) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "60px",
            background: "white",
            borderRadius: "20px",
            border: "1px solid #e5e7eb",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.5 }}>
            📊
          </div>
          <p style={{ color: "#94a3b8", fontSize: "16px" }}>
            No sentiment data available
          </p>
        </div>
      );
    }

    const overallSentiment = data["Overall Sentiment"];
    const emotionBreakdown = data["Sentiment Distribution"];

    return (
      <div>
        {/* Overall Sentiment Mega-Card */}
        {overallSentiment && (
          <div
            style={{
              marginBottom: "32px",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                "0 30px 80px rgba(0, 0, 0, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 25px 70px rgba(0, 0, 0, 0.4)";
            }}
          >
            <div
              style={{
                background: `linear-gradient(135deg, ${getSentimentColor(
                  overallSentiment.Classification
                )}25 0%, ${getSentimentColor(
                  overallSentiment.Classification
                )}15 50%, rgba(255, 255, 255, 0.05) 100%)`,
                borderRadius: "28px",
                padding: "40px",
                border: `2px solid ${getSentimentColor(
                  overallSentiment.Classification
                )}50`,
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                backdropFilter: "blur(20px)",
                transition: "all 0.3s ease",
              }}
            >
              {/* Emoji Watermark */}
              <div
                style={{
                  position: "absolute",
                  top: "-30px",
                  right: "-30px",
                  fontSize: "160px",
                  opacity: "0.08",
                  pointerEvents: "none",
                }}
              >
                {getSentimentEmoji(overallSentiment.Classification)}
              </div>

              {/* Content */}
              <div style={{ position: "relative", zIndex: 1 }}>
                <div
                  style={{
                    fontSize: "13px",
                    color: getSentimentColor(overallSentiment.Classification),
                    fontWeight: "800",
                    textTransform: "uppercase",
                    letterSpacing: "0.15em",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>
                    {getSentimentEmoji(overallSentiment.Classification)}
                  </span>
                  Overall Sentiment
                </div>
                <div
                  style={{
                    fontSize: "56px",
                    fontWeight: "900",
                    color: getSentimentColor(overallSentiment.Classification),
                    lineHeight: "1",
                    marginBottom: "24px",
                    textShadow: `0 0 30px ${getSentimentColor(
                      overallSentiment.Classification
                    )}40`,
                  }}
                >
                  {overallSentiment.Classification}
                </div>

                {/* Scores Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "24px",
                    marginTop: "28px",
                  }}
                >
                  {overallSentiment["Sentiment Score"] !== undefined && (
                    <div
                      style={{
                        background: "white",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#94a3b8",
                          marginBottom: "8px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Sentiment Score
                      </div>
                      <div
                        style={{
                          fontSize: "40px",
                          fontWeight: "900",
                          color: getSentimentColor(
                            overallSentiment.Classification
                          ),
                          fontFamily: "'Space Mono', monospace",
                          lineHeight: "1",
                        }}
                      >
                        {getNumericValue(overallSentiment["Sentiment Score"])}
                        <span style={{ fontSize: "24px" }}>/10</span>
                      </div>
                    </div>
                  )}
                  {overallSentiment["Confidence Score"] !== undefined && (
                    <div
                      style={{
                        background: "white",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#94a3b8",
                          marginBottom: "8px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Confidence
                      </div>
                      <div
                        style={{
                          fontSize: "40px",
                          fontWeight: "900",
                          color: getSentimentColor(
                            overallSentiment.Classification
                          ),
                          fontFamily: "'Space Mono', monospace",
                          lineHeight: "1",
                        }}
                      >
                        {getNumericValue(overallSentiment["Confidence Score"])}
                        <span style={{ fontSize: "24px" }}>%</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sentiment Distribution with Animated Bars */}
        {emotionBreakdown && (
          <div
            style={{
              background: "white",
              borderRadius: "28px",
              padding: "36px",
              border: "1px solid #e5e7eb",
              backdropFilter: "blur(20px)",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              marginBottom: "32px",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow =
                "0 30px 80px rgba(0, 0, 0, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 25px 70px rgba(0, 0, 0, 0.4)";
            }}
          >
            <h3
              style={{
                fontSize: "28px",
                fontWeight: "900",
                marginBottom: "32px",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <span style={{ fontSize: "32px" }}>🎭</span> Sentiment
              Distribution
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "20px",
              }}
            >
              {Object.entries(emotionBreakdown).map(([emotion, score]) => {
                const numScore = getNumericValue(score);
                const color =
                  numScore > 60
                    ? "#e04665"
                    : numScore > 30
                    ? "#3b82f6"
                    : "#64748b";

                return (
                  <div
                    key={emotion}
                    style={{
                      background: `linear-gradient(135deg, ${color}15 0%, rgba(255, 255, 255, 0.03) 100%)`,
                      border: `1px solid ${color}40`,
                      borderRadius: "20px",
                      padding: "24px",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.borderColor = `${color}70`;
                      e.currentTarget.style.boxShadow = `0 20px 40px ${color}20`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor = `${color}40`;
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        color: "#4b5563",
                        marginBottom: "16px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {emotion}
                    </div>
                    <div
                      style={{
                        fontSize: "44px",
                        fontWeight: "900",
                        color: color,
                        fontFamily: "'Space Mono', monospace",
                        marginBottom: "16px",
                        lineHeight: "1",
                      }}
                    >
                      {numScore}
                      <span style={{ fontSize: "24px" }}>%</span>
                    </div>

                    {/* Animated Progress Bar */}
                    <div
                      style={{
                        width: "100%",
                        height: "10px",
                        background: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "5px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: `${numScore}%`,
                          height: "100%",
                          background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`,
                          borderRadius: "5px",
                          transition: "width 1.5s ease-out",
                          boxShadow: `0 0 15px ${color}60`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Other Sentiment Sections Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
            gap: "24px",
          }}
        >
          {Object.entries(data)
            .filter(
              ([key]) =>
                key !== "Overall Sentiment" && key !== "Sentiment Distribution"
            )
            .map(([sectionKey, sectionData]) => {
              if (!sectionData || typeof sectionData !== "object") return null;

              const sectionColor = getSectionColor(sectionKey);

              return (
                <div
                  key={sectionKey}
                  style={{
                    background: "white",
                    borderRadius: "20px",
                    padding: "32px",
                    border: "1px solid #e5e7eb",
                    backdropFilter: "blur(20px)",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 24px rgba(0, 0, 0, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 6px rgba(0, 0, 0, 0.1)";
                  }}
                >
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      marginBottom: "24px",
                      color: sectionColor,
                      borderBottom: `3px solid ${sectionColor}`,
                      paddingBottom: "12px",
                      textTransform:
                        sectionKey === "Key Emotional Moments"
                          ? "none"
                          : "none",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {sectionKey}
                  </h3>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    {Object.entries(sectionData).map(([key, value]) => (
                      <div key={key}>
                        {/* Show subtitle - but skip for Key Emotional Moments array */}
                        {!(
                          sectionKey === "Key Emotional Moments" &&
                          Array.isArray(value)
                        ) &&
                          (typeof value === "string" ||
                            Array.isArray(value) ||
                            typeof value === "object") && (
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#6b7280",
                                marginBottom: "10px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {key}
                            </div>
                          )}

                        {Array.isArray(value) ? (
                          // Special rendering for Key Emotional Moments array
                          sectionKey === "Key Emotional Moments" ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                              }}
                            >
                              {value.map((emotionItem, idx) => {
                                if (
                                  typeof emotionItem === "object" &&
                                  emotionItem !== null
                                ) {
                                  return (
                                    <div
                                      key={idx}
                                      style={{
                                        background: "#fef3f2",
                                        border: "1px solid #fecaca",
                                        borderLeft: "4px solid #ef4444",
                                        borderRadius: "12px",
                                        padding: "20px",
                                        marginBottom: "12px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "flex-start",
                                          marginBottom: "12px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "13px",
                                            fontWeight: "700",
                                            color: "#ef4444",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.05em",
                                          }}
                                        >
                                          {emotionItem.emotion ||
                                            emotionItem.EMOTION ||
                                            "Emotion"}
                                        </div>
                                        {(emotionItem.timestamp ||
                                          emotionItem.TIMESTAMP) && (
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#9ca3af",
                                              fontWeight: "600",
                                            }}
                                          >
                                            {emotionItem.timestamp ||
                                              emotionItem.TIMESTAMP}
                                          </div>
                                        )}
                                      </div>

                                      {(emotionItem.quote ||
                                        emotionItem.QUOTE) && (
                                        <div
                                          style={{
                                            fontSize: "15px",
                                            fontStyle: "italic",
                                            color: "#1f2937",
                                            marginBottom: "12px",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          "
                                          {emotionItem.quote ||
                                            emotionItem.QUOTE}
                                          "
                                        </div>
                                      )}

                                      {(emotionItem.significance ||
                                        emotionItem.SIGNIFICANCE) && (
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            color: "#6b7280",
                                            lineHeight: "1.6",
                                            paddingTop: "12px",
                                            borderTop: "1px solid #fecaca",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "11px",
                                              fontWeight: "700",
                                              color: "#9ca3af",
                                              textTransform: "uppercase",
                                              letterSpacing: "0.05em",
                                              display: "block",
                                              marginBottom: "6px",
                                            }}
                                          >
                                            Significance
                                          </span>
                                          {emotionItem.significance ||
                                            emotionItem.SIGNIFICANCE}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          ) : (
                            // Regular array rendering for other sections
                            <ul
                              style={{
                                margin: 0,
                                paddingLeft: "24px",
                                color: "#4b5563",
                                fontSize: "14px",
                                lineHeight: "1.8",
                              }}
                            >
                              {value.map((item, idx) => (
                                <li
                                  key={idx}
                                  style={{
                                    marginBottom: "8px",
                                    paddingLeft: "8px",
                                  }}
                                >
                                  {typeof item === "object"
                                    ? JSON.stringify(item, null, 2)
                                    : String(item)}
                                </li>
                              ))}
                            </ul>
                          )
                        ) : typeof value === "object" && value !== null ? (
                          // Regular nested object display for other sections
                          <div
                            style={{
                              color: "#4b5563",
                              fontSize: "14px",
                              lineHeight: "1.7",
                              background: "#f9fafb",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            {Object.entries(value).map(([subKey, subValue]) => (
                              <div
                                key={subKey}
                                style={{
                                  marginBottom: "12px",
                                  paddingLeft: "12px",
                                  borderLeft: "3px solid #e04665",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    fontWeight: "700",
                                    marginBottom: "6px",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                  }}
                                >
                                  {subKey}
                                </div>
                                <div
                                  style={{
                                    color: "#1f2937",
                                    fontSize: "14px",
                                    lineHeight: "1.6",
                                  }}
                                >
                                  {typeof subValue === "object"
                                    ? JSON.stringify(subValue, null, 2)
                                    : String(subValue)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p
                            style={{
                              color: "#4b5563",
                              fontSize: "15px",
                              margin: 0,
                              lineHeight: "1.7",
                              background: "#f9fafb",
                              padding: "12px 16px",
                              borderRadius: "12px",
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            {String(value)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  if (loading || roleLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#F5F3FF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Loader2
            className="w-16 h-16 animate-spin mx-auto mb-4"
            style={{ color: "#e04665" }}
          />
          <p style={{ fontSize: "18px", fontWeight: "600", color: "#6b21a8" }}>
            {roleLoading ? "Loading Permissions..." : "Loading Analytics..."}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#F5F3FF",
          padding: "40px",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 24px",
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            color: "#374151",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            marginBottom: "32px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <div
          style={{
            background: "white",
            border: "2px solid #fca5a5",
            borderRadius: "20px",
            padding: "32px",
            textAlign: "center",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>⚠️</div>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "700",
              marginBottom: "12px",
              color: "#991b1b",
            }}
          >
            Error Loading Analytics
          </h2>
          <p style={{ color: "#6b7280" }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F5F3FF",
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: "40px",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <button
          onClick={() => {
            if (analyticsType === "group") {
              navigate(`/groups/${groupId}`);
            } else if (analyticsType === "user") {
              navigate("/meetings");
            } else {
              navigate(-1);
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 24px",
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            color: "#374151",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            marginBottom: "24px",
            transition: "all 0.3s",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f9fafb";
            e.currentTarget.style.borderColor = "#e04665";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }}
        >
          <ArrowLeft size={20} />
          {analyticsType === "group"
            ? "Back to Group"
            : analyticsType === "user"
            ? "Back to Meetings"
            : "Back to Meeting"}
        </button>

        <div
          style={{
            display: "inline-block",
            padding: "8px 20px",
            background: "rgba(224, 71, 101, 0.1)",
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: "700",
            color: "#e04665",
            marginBottom: "16px",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
          }}
        >
          {analyticsType === "group"
            ? "Group Analytics"
            : analyticsType === "user"
            ? "My Analytics"
            : "Meeting Analytics"}
        </div>

        <h1
          style={{
            fontSize: "48px",
            fontWeight: "900",
            marginBottom: "12px",
            background:
              "linear-gradient(135deg, #6b21a8 0%, #9333ea 50%, #a855f7 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "-0.03em",
          }}
        >
          Performance & Sentiment Analysis
        </h1>

        <p
          style={{
            fontSize: "18px",
            color: "#6b7280",
            maxWidth: "700px",
            lineHeight: "1.6",
          }}
        >
          Deep insights into meeting performance and customer sentiment powered
          by AI
        </p>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "32px",
          background: "white",
          padding: "8px",
          borderRadius: "16px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        {[
          {
            id: "overview",
            label: "📊 Overview",
            icon: BarChart3,
            permission: "can_view_overview",
          },
          {
            id: "sales",
            label: "🎯 Sales Performance",
            icon: TrendingUp,
            permission: "can_view_sales_performance",
          },
          {
            id: "client",
            label: "👥 Client Insights",
            icon: BarChart3,
            permission: "can_view_client_insights",
          },
          {
            id: "sentiment",
            label: "😊 Sentiment",
            icon: SmilePlus,
            permission: "can_view_sentiment",
          },
        ]
          .filter(
            (tab) =>
              !tab.permission || (permissions && permissions[tab.permission])
          )
          .map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: "16px 24px",
                background: activeTab === tab.id ? "#e04665" : "transparent",
                border: "none",
                borderRadius: "12px",
                color: activeTab === tab.id ? "white" : "#6b7280",
                fontSize: "15px",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = "#f9fafb";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              {tab.label}
            </button>
          ))}
      </div>

      {/* Content */}
      <div>
        {activeTab === "overview" && (
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "24px",
                marginBottom: "32px",
              }}
            >
              {evaluationData?.sales_evaluation?.[
                "Overall Salesperson Performance Score"
              ] &&
                renderScoreCard(
                  "Sales Performance",
                  getNumericValue(
                    evaluationData.sales_evaluation[
                      "Overall Salesperson Performance Score"
                    ]
                  ),
                  10,
                  "🎯",
                  "#e04665"
                )}

              {sentimentData?.["Overall Sentiment"]?.Classification && (
                <div
                  style={{
                    background: "white",
                    borderRadius: "20px",
                    padding: "32px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: getSentimentColor(
                        sentimentData["Overall Sentiment"].Classification
                      ),
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      marginBottom: "12px",
                    }}
                  >
                    Customer Sentiment
                  </div>
                  <div
                    style={{
                      fontSize: "48px",
                      marginBottom: "12px",
                    }}
                  >
                    {getSentimentEmoji(
                      sentimentData["Overall Sentiment"].Classification
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      color: getSentimentColor(
                        sentimentData["Overall Sentiment"].Classification
                      ),
                    }}
                  >
                    {sentimentData["Overall Sentiment"].Classification}
                  </div>
                </div>
              )}

              {sentimentData?.["Overall Sentiment"]?.["Confidence Score"] &&
                renderScoreCard(
                  "Confidence",
                  getNumericValue(
                    sentimentData["Overall Sentiment"]["Confidence Score"]
                  ),
                  100,
                  "💯",
                  "#3b82f6"
                )}
            </div>
          </div>
        )}

        {activeTab === "sales" &&
          evaluationData?.sales_evaluation &&
          renderSalesEvaluation(evaluationData.sales_evaluation)}

        {activeTab === "client" && (
          /* Normal User: Show meeting-specific overview */
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "24px",
                marginBottom: "32px",
              }}
            >
              {evaluationData?.sales_evaluation?.[
                "Overall Salesperson Performance Score"
              ] &&
                renderScoreCard(
                  "Sales Performance",
                  getNumericValue(
                    evaluationData.sales_evaluation[
                      "Overall Salesperson Performance Score"
                    ]
                  ),
                  10,
                  "🎯",
                  "#e04665"
                )}

              {sentimentData?.["Overall Sentiment"]?.Classification && (
                <div
                  style={{
                    background: "white",
                    borderRadius: "20px",
                    padding: "32px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: getSentimentColor(
                        sentimentData["Overall Sentiment"].Classification
                      ),
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      marginBottom: "12px",
                    }}
                  >
                    Customer Sentiment
                  </div>
                  <div
                    style={{
                      fontSize: "48px",
                      marginBottom: "12px",
                    }}
                  >
                    {getSentimentEmoji(
                      sentimentData["Overall Sentiment"].Classification
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      color: getSentimentColor(
                        sentimentData["Overall Sentiment"].Classification
                      ),
                    }}
                  >
                    {sentimentData["Overall Sentiment"].Classification}
                  </div>
                </div>
              )}

              {sentimentData?.["Overall Sentiment"]?.["Confidence Score"] &&
                renderScoreCard(
                  "Confidence",
                  getNumericValue(
                    sentimentData["Overall Sentiment"]["Confidence Score"]
                  ),
                  100,
                  "💯",
                  "#3b82f6"
                )}
            </div>

            {/* Client Insights Section */}
            {evaluationData?.client_evaluation &&
            Object.keys(evaluationData.client_evaluation).length > 0 ? (
              renderClientEvaluation(evaluationData.client_evaluation)
            ) : (
              <div
                style={{
                  background: "white",
                  borderRadius: "24px",
                  padding: "64px 32px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  textAlign: "center",
                  marginTop: "32px",
                }}
              >
                <div
                  style={{
                    fontSize: "64px",
                    marginBottom: "24px",
                    opacity: 0.3,
                  }}
                >
                  📊
                </div>
                <h3
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#1f2937",
                    marginBottom: "12px",
                  }}
                >
                  No Client Insights Available
                </h3>
                <p
                  style={{
                    fontSize: "16px",
                    color: "#6b7280",
                    maxWidth: "500px",
                    margin: "0 auto",
                  }}
                >
                  Evaluations are not available for this meeting. They may still
                  be processing or the meeting hasn't ended yet.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "sentiment" &&
          sentimentData &&
          renderSentimentAnalysis(sentimentData)}
      </div>
    </div>
  );
};

export default MeetingAnalytics;
